import PageIntroBand from 'components/PageIntroBand';
import { FlowButton } from 'components/ui/flow-button';
import HeroBackground from 'components/HeroBackground';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, FileDown, Loader2, MailCheck } from 'lucide-react';
import { useReveal } from 'hooks/useReveal';
import { useSiteContent } from 'hooks/useSiteContent';
import HeroScrollRail from 'components/HeroScrollRail';
import SiteHeroCtas, {
  HERO_CTA_PRIMARY_CLASS,
  HERO_CTA_SECONDARY_CLASS,
  PAGE_HERO_BODY_CLASS,
  PAGE_HERO_BODY_TEXT_CLASS,
  PAGE_HERO_CONTAINER_CLASS,
  PAGE_HERO_CONTENT_CLASS,
  PAGE_HERO_CTA_ROW_CLASS,
  PAGE_HERO_EYEBROW_CLASS,
  PAGE_HERO_H1_CLASS,
  PAGE_HERO_SECTION_CLASS,
} from 'components/SiteHeroCtas';
import {
  extractCarrierFlowReference,
  submitResourceDownloadRequest,
} from 'lib/sitePublicApi';
import { ResourcesLandingDesign, ResourceDetailDesign } from 'components/resources/ResourcePageDesign';
import ServiceDetailDesign from 'components/services/ServiceDetailDesign';
import { getPublishedResourcePageBySlug } from 'lib/resourceCms';
import { useServices } from 'hooks/useServices';
import ServiceCatalog from 'components/services/ServiceCatalog';
import { guardServicesHero } from 'lib/servicesPlacement';
import ServicesTheme from 'components/services/ServicesTheme';
import { DEFAULT_SITE_CONTENT, splitSiteContentParagraphs } from 'lib/siteContentCms';

const RESOURCES_FAQS = [
  ['Are these resources the full paperwork system?', 'No. Free resources are gap-checking tools. The full system is delivered through Basic, Standard, or Premium paperwork packages.'],
  ['What happens after I download a resource?', 'Use the resource to review your current records. If the issue is small, fix it. If the resource reveals scattered ownership, missing review dates, repeated problems, or unclear responsibility, compare the paperwork packages.'],
  ['Do resources include legal advice?', 'No. Magellan resources are educational and operational. They are not legal advice, agency representation, citation defense, court representation, penalty response, safety rating appeal, employment-law guidance, contract interpretation, or state-specific legal analysis.'],
  ['Which package should I compare after using a resource?', 'Use Basic if you need starter cleanup and baseline file discipline. Compare Standard if the issue involves ownership, audit structure, recurring problems, or management control. Request Premium Review if you are preparing for stricter freight, high-value cargo, reefer operations, pharma-style expectations, or shipper review.'],
  ['Can I use resources without buying a package?', 'Yes. The resources are meant to help carriers identify gaps. A package becomes the next step when the gap is not isolated and the carrier needs a stronger operating structure.'],
];
const RESOURCES_HERO_CONTENT = {
  eyebrow: 'Free resources for carrier paperwork',
  title: 'Start with a checklist. See where the work needs structure.',
  body: 'Use free resources to review your current records and identify the next practical step for your company.',
  primaryCtaLabel: 'Browse Free Resources',
  primaryCtaPath: '#resource-library',
  secondaryCtaLabel: 'View Paperwork Packages',
  secondaryCtaPath: '/paperwork',
};
const RESOURCES_MARQUEE_ITEMS = [
  'Resource library',
  'Driver files',
  'Audit readiness',
  'Safety follow-up',
  'DataQs',
  'SOPs',
  'Training',
  'TMS workflows',
  'Management review',
];
const RESOURCE_PRESSURE_AREAS = [
  [
    'Driver files and onboarding',
    'Check whether DQF records, expirations, inquiries, annual reviews, medical cards, MVRs, and driver status controls are actually organized.',
    'Best next step: View DQF resources or compare the Basic and Standard packages.',
  ],
  [
    'HOS / ELD records',
    'Review supporting documents, edits, unassigned miles, personal conveyance, yard moves, log certifications, and weekly review habits.',
    'Best next step: View HOS resources or compare the Standard package.',
  ],
  [
    'Maintenance / DVIR / annual inspection records',
    'Check whether inspections, DVIR defects, annual inspections, repair records, PM schedules, and OOS proof are tied to unit files.',
    'Best next step: View Maintenance resources or compare the Standard package.',
  ],
  [
    'DOT audit and binder readiness',
    'Use binder, document-request, and missing-record checklists to identify scattered files before an audit request becomes a scramble.',
    'Best next step: View Audit resources or compare the Standard and Premium paperwork packages.',
  ],
  [
    'DataQs / CSA / inspection follow-up',
    'Screen whether the issue is data review, evidence, root cause, corrective action, or management rhythm.',
    'Best next step: View DataQs resources or view related training.',
  ],
  [
    'Roadside inspection readiness',
    'Review driver documents, ELD transfer readiness, repair proof, post-inspection response, and inspection/citation follow-up.',
    'Best next step: View Roadside resources or compare the Standard package.',
  ],
  [
    'Authority / registration / credentials',
    'Check whether MCS-150, authority status, insurance/BOC-3, portal access, renewal dates, and credential ownership are tracked.',
    'Best next step: View Authority resources or compare the Basic and Standard packages.',
  ],
  [
    'Premium freight documentation',
    'Use premium-readiness resources to review whether high-value, reefer, pharma-style, security, temperature, vendor, or shipper documentation expectations are starting to exceed your current paperwork system.',
    'Best next step: Request Premium Review.',
  ],
];
const CHECKLIST_LIMITS = [
  ['One missing document:', 'Fix the document.'],
  ['Repeated missing documents:', 'Compare Basic.'],
  ['Recurring operational gaps:', 'Compare Standard.'],
  ['Strict shipper or premium freight expectations:', 'Request Premium Review.'],
  ['Active audit, serious dispute, or unclear scope:', 'Discuss the agreed paperwork scope.'],
];
const SERVICES_HERO_SECTION_CLASS = 'relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px]';
const SERVICES_MARQUEE_ITEMS = [
  "ELD support",
  "Safety staff",
  "Direct-hire recruitment",
  "Candidate training",
  "Monthly reviews",
  "Corrective actions",
  "CarrierFlow TMS",
  "Team responsibilities"
];

function renderGoldHeroTitle(title) {
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  const goldStartIndex = Math.max(0, words.length - 3);
  const regularWords = words.slice(0, goldStartIndex);
  const goldPhrase = words.slice(goldStartIndex).join(' ');

  return (
    <>
      {regularWords.map((word, index) => (
        <span key={`${word}-${index}`} aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
          <span className="inline-block">
            {word}
          </span>
        </span>
      ))}
      <span aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
        <span className="inline-block italic shimmer-text">
          {goldPhrase}
        </span>
      </span>
    </>
  );
}

function PageShell({ children, className = '' }) {
  const ref = useReveal();
  return <div ref={ref} className={`min-h-screen bg-canvas ${className}`}>{children}</div>;
}

const resourceFadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
};

const resourceViewport = { once: true, amount: 0.22 };

function ResourceGridGlow({ accent = '#4c8dcc', className = '' }) {
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.72) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.72) 1px, transparent 1px)',
          backgroundSize: '58px 58px',
          maskImage: 'radial-gradient(ellipse at center, black, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black, transparent 78%)',
        }}
      />
      <div
        className="absolute -top-40 right-[-12%] h-[460px] w-[680px] rounded-full blur-[150px] opacity-[0.18]"
        style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }}
      />
    </div>
  );
}

function ResourcesIntroBand() {
  return (
    <PageIntroBand
      testId="resources-page-intro"
      label="Free resources"
      title="Own the next step."
      backgroundText="RESOURCES"
      paragraphs={[
        "Use a checklist to identify records needing attention, assign the follow-up and set a review date.",
        "A resource can address an isolated gap. Recurring missing records or unclear responsibilities may need a company-wide paperwork structure.",
      ]}
    />
  );
}

function ServicesIntroBand() {
  const content = useSiteContent('servicesIntroduction');
  // Normalize only the exact legacy fallback, preserving custom published copy.
  const body = !content.body || content.body === "ELD Support, Safety Support, Monthly Reviews and TMS services help your company manage the work beyond its paperwork.\n\nChoose the support your operation needs. We review your current setup and agree the scope, responsibilities and next steps with your team."
    ? DEFAULT_SITE_CONTENT.servicesIntroduction.body : content.body;
  const paragraphs = splitSiteContentParagraphs(body);
  return (
    <PageIntroBand
      testId="services-page-intro"
      label={content.eyebrow || DEFAULT_SITE_CONTENT.servicesIntroduction.eyebrow}
      title={content.title || 'Choose the support your operation needs.'}
      backgroundText="SERVICES"
      paragraphs={[paragraphs[0], paragraphs.slice(1).join(' ')]}
    />
  );
}

function Hero({
  eyebrow,
  title,
  description,
  descriptionParagraphs = [],
  ctaLabel = 'Company Review',
  ctaPath = '/companies/apply',
  imageUrl = '',
  imageAlt = '',
  ctaContent = null,
  imageClassName = 'absolute inset-0 bg-cover bg-center opacity-18',
  overlayClassName = 'absolute inset-0 bg-gradient-to-br from-canvas via-surface/95 to-canvas/85',
  secondaryOverlayClassName = '',
}) {
  const paragraphs = descriptionParagraphs.length ? descriptionParagraphs : [description].filter(Boolean);

  return (
    <section className={PAGE_HERO_SECTION_CLASS}>
      {imageUrl ? (
        <div data-hero-parallax="true" className={imageClassName} style={{ backgroundImage: `url(${imageUrl})` }} role="img" aria-label={imageAlt} />
      ) : null}
      <div className={overlayClassName} />
      {secondaryOverlayClassName ? <div className={secondaryOverlayClassName} /> : null}
      <div className="absolute inset-0 grain-overlay" />
      <div className={PAGE_HERO_CONTAINER_CLASS}>
        <div className={PAGE_HERO_CONTENT_CLASS}>
          <p className={PAGE_HERO_EYEBROW_CLASS}>{eyebrow}</p>
          <h1 className={PAGE_HERO_H1_CLASS}>
            {title}
          </h1>
          {paragraphs.length > 1 ? (
            <div className={`${PAGE_HERO_BODY_CLASS} visible`}>
              {paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          ) : (
            <p className={`${PAGE_HERO_BODY_TEXT_CLASS} visible`}>
              {paragraphs[0]}
            </p>
          )}
          {ctaContent ? (
            <SiteHeroCtas
              content={ctaContent}
              className={PAGE_HERO_CTA_ROW_CLASS}
              primaryClassName={HERO_CTA_PRIMARY_CLASS}
              secondaryClassName={HERO_CTA_SECONDARY_CLASS}
              tertiaryClassName={HERO_CTA_SECONDARY_CLASS}
            />
          ) : (
            <FlowButton
              to={ctaPath}
              className={`reveal reveal-delay-3 visible ${HERO_CTA_PRIMARY_CLASS}`}
            >
              {ctaLabel}
            </FlowButton>
          )}
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="max-w-3xl mb-12">
      <p className="reveal text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">{eyebrow}</p>
      <h2 className="reveal reveal-delay-1 font-display text-3xl sm:text-4xl tracking-tight text-ink mb-6">{title}</h2>
      {description ? <p className="reveal reveal-delay-2 font-body text-copy/50 leading-relaxed">{description}</p> : null}
    </div>
  );
}

export function ServicesPage() {
  const hero = guardServicesHero(useSiteContent('servicesHero'), DEFAULT_SITE_CONTENT.servicesHero);
  const heroParagraphs = splitSiteContentParagraphs(hero.body);

  return (
    <ServicesTheme><PageShell>
      <section data-theme-surface="dark" data-testid="services-hero" className={`${SERVICES_HERO_SECTION_CLASS} site-image-hero`}>
        <HeroBackground imageUrl={hero.imageUrl} imageAlt={hero.imageAlt} />
        <div className="absolute inset-0 grain-overlay" />
        <div data-hero-text-parallax="true" className={PAGE_HERO_CONTAINER_CLASS}>
          <div className={PAGE_HERO_CONTENT_CLASS} aria-busy={hero.__loading || undefined}>
            <p className={PAGE_HERO_EYEBROW_CLASS}>{hero.eyebrow}</p>
            <h1 aria-label={hero.title} className={PAGE_HERO_H1_CLASS}>
              {renderGoldHeroTitle(hero.title)}
            </h1>
            <div className={`${PAGE_HERO_BODY_CLASS} visible`}>
              {heroParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            <SiteHeroCtas
              content={hero}
              className={PAGE_HERO_CTA_ROW_CLASS}
              primaryClassName={HERO_CTA_PRIMARY_CLASS}
              secondaryClassName={HERO_CTA_SECONDARY_CLASS}
              tertiaryClassName={HERO_CTA_SECONDARY_CLASS}
            />
          </div>
        </div>
        <HeroScrollRail targetId="core-service-areas" />
      </section>

      <div data-testid="services-moving-strip" className="topic-rail overflow-hidden border-y border-ink/10 bg-canvas">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...SERVICES_MARQUEE_ITEMS, ...SERVICES_MARQUEE_ITEMS].map((item, index) => (
              <span key={`${item}-${index}`} className="mx-6 font-body text-[10px] uppercase tracking-[0.32em] text-copy/42">
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      <ServicesIntroBand />

      <ServiceCatalog />
    </PageShell></ServicesTheme>
  );
}

export function ServiceDetailPage({ serviceKey }) {
  const params = useParams();
  const resolvedSlug = serviceKey || params.slug || 'paperwork-compliance';
  const state = useServices(resolvedSlug, true);
  return <ServicesTheme><PageShell className="support-pages service-detail"><ServiceDetailDesign state={state} /></PageShell></ServicesTheme>;
}

const INITIAL_DOWNLOAD_FORM = {
  contactName: '',
  email: '',
  phone: '',
  companyName: '',
  dotNumber: '',
  mcNumber: '',
  fleetSize: '',
  employeeCount: '',
  companyZipCode: '',
  operationType: '',
  message: '',
  website: '',
};

function DownloadLeadForm({ sourceType = 'service', source, offer, submitRequest }) {
  const isResource = sourceType === 'resource';
  const [form, setForm] = useState({
    ...INITIAL_DOWNLOAD_FORM,
  });
  const [step, setStep] = useState(1);
  const [submitState, setSubmitState] = useState('idle');
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleContinue = () => {
    setError('');
    setReference('');

    if (!form.contactName.trim() || !form.email.trim() || !form.companyName.trim() || (isResource && !form.phone.trim())) {
      setError(isResource ? 'Name, email, company, and phone are required before continuing.' : 'Name, email, and company are required before continuing.');
      return;
    }

    setStep(2);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setReference('');

    if (form.website) {
      setError('Unable to submit this request.');
      setSubmitState('error');
      return;
    }

    if (isResource && !form.phone.trim()) {
      setError('Phone is required before requesting this resource download.');
      setStep(1);
      return;
    }

    if (isResource && !form.dotNumber.trim() && !form.mcNumber.trim()) {
      setError('DOT number or MC number is required before requesting this resource download.');
      return;
    }

    setSubmitState('submitting');
    const result = await submitRequest(form, { [isResource ? 'resource' : 'service']: source, offer });
    if (result.success) {
      setReference(extractCarrierFlowReference(result.data));
      setSubmitState('submitted');
      setForm({
        ...INITIAL_DOWNLOAD_FORM,
      });
      setStep(1);
      return;
    }

    setError(result.error || 'Unable to submit this request.');
    setSubmitState('error');
  };

  return (
    <section className="support-download-form relative overflow-hidden py-20 md:py-28 bg-[#070808] border-y border-ink/10">
      <ResourceGridGlow className="opacity-80" />
      <div className="site-content-shell relative grid grid-cols-1 lg:grid-cols-[0.86fr_1.14fr] gap-10 lg:gap-14 items-start">
        <motion.div
          variants={resourceFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={resourceViewport}
          className="lg:sticky lg:top-28 rounded-2xl border border-gold/25 bg-black/40 p-7 md:p-8"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-gold/[0.06] px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-accent-ink font-body mb-6">
            <FileDown className="w-3.5 h-3.5" />
            Free paperwork download
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-ink tracking-tight leading-tight mb-5">{offer.title}</h2>
          <p className="font-body text-copy/48 leading-relaxed mb-6">{offer.description}</p>
          <div className="grid grid-cols-1 gap-3 border-t border-ink/8 pt-6">
            {offer.fileName ? (
              <div className="rounded-lg border border-ink/8 bg-ink/[0.03] px-4 py-3">
                <p className="font-body text-[10px] uppercase tracking-[0.2em] text-copy/32">Package</p>
                <p className="mt-1 font-body text-copy/70 text-sm leading-relaxed">{offer.fileName}</p>
              </div>
            ) : null}
            {offer.fileVersion ? (
              <div className="rounded-lg border border-ink/8 bg-ink/[0.03] px-4 py-3">
                <p className="font-body text-[10px] uppercase tracking-[0.2em] text-copy/32">Version</p>
                <p className="mt-1 font-body text-copy/70 text-sm leading-relaxed">{offer.fileVersion}</p>
              </div>
            ) : null}
            <div className="flex items-start gap-3 rounded-lg border border-emerald-400/15 bg-emerald-500/[0.04] px-4 py-3">
              <MailCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-300" strokeWidth={1.5} />
              <p className="font-body text-copy/48 text-xs leading-relaxed">The download link is sent only after you confirm your email.</p>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          variants={resourceFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={resourceViewport}
          className="rounded-2xl border border-ink/10 bg-ink/[0.025] p-6 md:p-8"
        >
          <div className="mb-6 flex items-center gap-3">
            {[1, 2].map((item) => (
              <div key={item} className={`flex h-9 w-9 items-center justify-center rounded-full border font-body text-xs ${step === item ? 'border-gold bg-gold text-obsidian' : 'border-ink/14 bg-black/30 text-copy/35'}`}>
                {item}
              </div>
            ))}
            <p className="font-body text-[10px] uppercase tracking-[0.22em] text-copy/35">
              {step === 1 ? 'Basic information' : 'Company details'}
            </p>
          </div>

          {step === 1 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">Name</span>
                  <input required value={form.contactName} onChange={(event) => updateForm('contactName', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">Email</span>
                  <input required type="email" value={form.email} onChange={(event) => updateForm('email', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">Company</span>
                  <input required value={form.companyName} onChange={(event) => updateForm('companyName', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">Phone</span>
                  <input required={isResource} value={form.phone} onChange={(event) => updateForm('phone', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" />
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">DOT number</span>
                  <input value={form.dotNumber} onChange={(event) => updateForm('dotNumber', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" placeholder="DOT 1234567" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">MC number</span>
                  <input value={form.mcNumber} onChange={(event) => updateForm('mcNumber', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" placeholder="MC 765432" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">Number of trucks</span>
                  <input value={form.fleetSize} onChange={(event) => updateForm('fleetSize', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">Employees</span>
                  <input value={form.employeeCount} onChange={(event) => updateForm('employeeCount', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">Company ZIP code</span>
                  <input value={form.companyZipCode} onChange={(event) => updateForm('companyZipCode', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">Operation type</span>
                  <input value={form.operationType} onChange={(event) => updateForm('operationType', event.target.value)} className="w-full rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm text-ink outline-none focus:border-gold/60" placeholder="Dry van, reefer, flatbed, local, OTR..." />
                </label>
              </div>
              <label className="block mb-6">
                <span className="text-[10px] uppercase tracking-[0.22em] text-copy/35 font-body mb-2 block">What are you trying to fix?</span>
                <textarea rows={4} value={form.message} onChange={(event) => updateForm('message', event.target.value)} className="w-full resize-y rounded-lg border border-ink/12 bg-black/35 px-4 py-3 text-sm leading-7 text-ink outline-none focus:border-gold/60" />
              </label>
            </>
          )}
          <label className="hidden" aria-hidden="true">
            Website
            <input tabIndex="-1" autoComplete="off" value={form.website} onChange={(event) => updateForm('website', event.target.value)} />
          </label>

          {error ? (
            <div className="mb-5 border border-red-500/20 bg-red-500/5 p-4">
              <p className="font-body text-red-200 text-sm leading-relaxed">{error}</p>
            </div>
          ) : null}

          {submitState === 'submitted' ? (
            <div className="mb-5 border border-emerald-400/20 bg-emerald-500/5 p-4">
              <p className="font-body text-emerald-200 text-sm leading-relaxed">
                {offer.successMessage || 'Your request was received. Check your email to confirm access.'}
                {reference ? ` Reference: ${reference}` : ''}
              </p>
            </div>
          ) : null}

          {step === 1 ? (
            <FlowButton type="button" onClick={handleContinue} className="inline-flex w-full items-center justify-center gap-3   px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-body font-bold  transition-all">
              Continue
            </FlowButton>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-[0.35fr_0.65fr] gap-3">
              <FlowButton type="button" onClick={() => setStep(1)} className="inline-flex items-center justify-center border border-ink/14 px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-copy/55  hover:border-gold/35 transition-colors">
                Back
              </FlowButton>
              <FlowButton type="submit" disabled={submitState === 'submitting'} className="inline-flex items-center justify-center gap-3   px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-body font-bold  transition-all disabled:opacity-50">
                {submitState === 'submitting' ? (
                  <>Submitting <Loader2 className="w-4 h-4 animate-spin" /></>
                ) : (
                  <>{offer.buttonLabel || 'Request Download'} </>
                )}
              </FlowButton>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}

function ResourceDownloadLeadForm({ resource, offer }) {
  return (
    <DownloadLeadForm
      sourceType="resource"
      source={resource}
      offer={offer}
      submitRequest={submitResourceDownloadRequest}
    />
  );
}

export function DataQsWebinarPage() {
  return (
    <PageShell>
      <Hero
        eyebrow="Complete course series"
        title="Buy all 10 courses for $1,499."
        description="Get the full Magellan course series for trucking companies at a discounted series price. The training covers driver files, DOT audits, DataQs, safety meetings, SOPs, onboarding, recruiting, TMS workflows, management visibility, and the operating system behind the company."
        ctaLabel="Buy Full Series"
        ctaPath="/webinars?webinar=complete-webinar-series"
      />
      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-16 items-start">
          <div>
            <SectionHeader eyebrow="Series offer" title="Ten practical sessions for the operating structure behind your trucking company" />
            <div className="space-y-5 font-body text-copy/50 leading-relaxed">
              <p>This offer replaces the old single DataQs course page. It is now the full 10-course training bundle for companies that want a structured path through the operating problems carriers usually notice too late.</p>
              <p>Use the series to understand what should be organized, what needs ownership, and what should be reviewed before small gaps become expensive problems.</p>
            </div>
          </div>
          <div className="border border-gold/20 bg-canvas p-8 md:p-10">
            <p className="text-[10px] uppercase tracking-[0.28em] text-accent-ink/75 font-body mb-4">Discounted series price</p>
            <div className="flex items-end gap-4 mb-6">
              <p className="font-display text-5xl text-accent-ink leading-none">$1,499</p>
              <p className="font-body text-copy/35 line-through pb-1">$1,999</p>
            </div>
            <p className="font-body text-copy/48 leading-relaxed mb-8">
              One enrollment for all 10 courses. Paid enrollment is unavailable until the direct TMS connection is verified.
            </p>
            <FlowButton to="/webinars?webinar=complete-webinar-series" className="inline-flex w-full items-center justify-center gap-3   px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-body font-bold  transition-all">
              Buy Full Series
            </FlowButton>
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="site-content-shell">
          <SectionHeader eyebrow="Included sessions" title="The full operating-structure path" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Driver Files & Onboarding Control',
              'DOT Audit Readiness & Compliance Folder Structure',
              'DataQs & Inspection Follow-Up',
              'Safety Meetings & Incident Review',
              'SOPs & Internal Workflows',
              'Driver Onboarding & Training Rhythm',
              'Recruiting Pipeline & Driver Retention',
              'TMS Workflows & Dispatch Consistency',
              'Management Visibility & Open Items Review',
              'Building the Operating System Behind the Company',
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 border border-ink/8 bg-surface p-5">
                <CheckCircle2 className="w-5 h-5 text-accent-ink mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <p className="font-body text-copy/58 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <SectionHeader eyebrow="Why the bundle" title="The sessions are stronger together because the problems are connected" />
            <div className="space-y-4">
              {[
                'Driver files connect to onboarding and training.',
                'Safety meetings connect to incident review and corrective action.',
                'Recruiting connects to onboarding and first-dispatch readiness.',
                'TMS workflow connects to management visibility.',
                'SOPs connect the daily work so the company does not run by memory.',
              ].map((item) => (
                <p key={item} className="font-body text-copy/48 text-sm leading-relaxed border-b border-ink/8 pb-4">{item}</p>
              ))}
            </div>
          </div>
          <div>
            <SectionHeader eyebrow="Who should use it" title="Built for carriers that want the full training path" />
            <div className="space-y-4">
              {[
                'Owners and leadership teams',
                'Safety and compliance managers',
                'Dispatch and operations leaders',
                'Recruiters and onboarding teams',
                'Admin staff responsible for records, follow-up, and documentation',
              ].map((item) => (
                <div key={item} className="border border-ink/8 bg-canvas p-4">
                  <p className="font-body text-copy/62 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="site-content-shell">
          <SectionHeader eyebrow="FAQ" title="Full series questions" />
          <div className="space-y-5">
            {[
              ['Is this only for large carriers?', 'No. Small and mid-sized carriers often need structure earlier because missing records, unclear follow-up, weak training, or inconsistent workflow can create serious damage.'],
              ['Can we buy only one course?', 'Yes. Single course enrollment is available from the main courses page. The full-series offer is for companies that want all 10 sessions at the discounted bundle price.'],
              ['Is this legal advice?', 'No. This is practical operating training. It does not replace qualified legal, regulatory, insurance, or professional guidance.'],
              ['Can Magellan help implement the systems after training?', 'Yes. A Company Review can identify where the same issues exist inside your operation and what should be built first.'],
            ].map(([question, answer]) => (
              <div key={question} className="border-b border-ink/8 pb-5">
                <h3 className="font-display text-xl text-ink mb-3">{question}</h3>
                <p className="font-body text-copy/45 text-sm leading-relaxed">{answer}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <FlowButton to="/webinars?webinar=complete-webinar-series" className="inline-flex items-center gap-3   px-10 py-4 text-[11px] uppercase tracking-[0.22em] font-body font-bold  transition-all">
              Buy Full Series
            </FlowButton>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

export function CompanyReviewPage() {
  return (
    <PageShell>
      <Hero
        eyebrow="Companies"
        title="Start with a company operating review."
        description="The company review is the starting point for trucking companies that need more structure but are not sure where the first project should begin."
        ctaLabel="Company Review"
        ctaPath="/companies/apply"
      />
      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <SectionHeader eyebrow="Why the review matters" title="Most carriers know something is not working before they know exactly what to fix" />
            <div className="space-y-5 font-body text-copy/50 leading-relaxed">
              <p>The owner may feel that the company is too dependent on one dispatcher. The safety manager may know the files are not where they should be. The recruiting process may be too slow. The TMS may not show the real status of the operation. Driver onboarding may change depending on who handles it.</p>
              <p>These are symptoms. The review looks for the operating cause behind those symptoms: ownership, workflow, records, approvals, handoffs, software discipline, management rhythm, and where the company depends too much on memory.</p>
            </div>
          </div>
          <div className="border border-gold/20 bg-canvas p-8 md:p-10">
            <h2 className="font-display text-3xl text-ink tracking-tight mb-6">The review is not a generic sales call.</h2>
            <p className="font-body text-copy/50 leading-relaxed">
              It is a structured review of the business behind the freight: paperwork, driver files, safety, training, recruiting, dispatch workflow, TMS usage, management visibility, and the operating habits that hold the company together.
            </p>
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="site-content-shell">
          <SectionHeader eyebrow="Review areas" title="What the operating review covers" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              ['Company structure', 'Fleet size, driver model, freight type, lanes, customer mix, growth stage, management roles, and where pressure is appearing.'],
              ['Paperwork', 'Driver files, compliance folders, policies, renewals, maintenance records, training records, audit preparation, and document ownership.'],
              ['Safety', 'Inspection follow-up, violation review, incident reporting, accident files, corrective action, coaching, claims documentation, and safety meetings.'],
              ['Training', 'Driver onboarding, first-dispatch readiness, recurring training, retraining, attendance records, completion tracking, and manager follow-up.'],
              ['Recruiting', 'Lead intake, qualification, document collection, approval, orientation, safety handoff, insurance handoff, and dispatch readiness.'],
              ['TMS workflow', 'Dispatch process, load status, document collection, billing readiness, driver communication, safety triggers, and dashboard visibility.'],
            ].map(([title, copy]) => (
              <div key={title} className="border border-ink/8 bg-surface p-7">
                <h2 className="font-display text-xl text-ink mb-4">{title}</h2>
                <p className="font-body text-copy/45 text-sm leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell">
          <SectionHeader eyebrow="Common findings" title="The patterns are common in growing carriers" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Driver files started but not completed.',
              'No reliable renewal tracker.',
              'No standard onboarding path.',
              'Safety meetings not documented.',
              'Corrective action handled informally.',
              'Claims documented inconsistently.',
              'Recruiting leads not tracked.',
              'Drivers reaching dispatch before paperwork is complete.',
              'TMS not connected to safety or billing.',
              'Loads closed with missing paperwork.',
              'No clear owner dashboard.',
              'Too much knowledge living in one person\'s head.',
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 border border-ink/8 bg-canvas p-5">
                <CheckCircle2 className="w-5 h-5 text-accent-ink mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <p className="font-body text-copy/58 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
          <p className="font-body text-copy/42 text-sm leading-relaxed mt-8 max-w-3xl">
            These findings are not unusual. They are common because operations usually grow faster than documentation. The purpose of the review is to identify what structure is missing and what should be built first.
          </p>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="site-content-shell">
          <SectionHeader eyebrow="Engagement paths" title="The review points to the right first project" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {[
              ['Compliance Foundation', 'Driver files, folders, policies, checklists, renewal tracking, and basic audit readiness.'],
              ['Structured Growth', 'Workflows that do not depend on one person\'s memory as the company adds trucks and staff.'],
              ['Safety Culture', 'Meetings, incident review, corrective action, training records, and management accountability.'],
              ['Specialized Freight', 'Preparation for pharma, temperature-sensitive, high-value, or documentation-heavy freight.'],
              ['TMS Workflow Control', 'Clean dispatch, paperwork, billing, safety, and management visibility around the software.'],
            ].map(([title, copy]) => (
              <div key={title} className="border border-ink/8 bg-surface p-6">
                <h2 className="font-display text-lg text-ink mb-4">{title}</h2>
                <p className="font-body text-copy/42 text-sm leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell">
          <SectionHeader eyebrow="Process" title="How the company review works" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
            {[
              ['Apply', 'Submit company information, fleet details, current systems, and the main problem.'],
              ['Schedule', 'Book a meeting so the situation can be reviewed properly.'],
              ['Diagnose', 'Magellan identifies the highest-risk gaps in the operating system.'],
              ['Recommend', 'You receive a practical recommendation for the correct first project.'],
              ['Build', 'If there is a fit, Magellan builds the documents, workflows, trackers, dashboards, and implementation plan.'],
            ].map(([title, copy], index) => (
              <div key={title} className="relative p-6 border-l border-gold/20 md:border-l-0 md:border-t md:border-gold/20">
                <span className="text-accent-ink font-display text-3xl font-bold opacity-30 block mb-4">{String(index + 1).padStart(2, '0')}</span>
                <h2 className="font-display text-lg text-ink mb-3">{title}</h2>
                <p className="font-body text-copy/42 text-sm leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <FlowButton to="/companies/apply" className="inline-flex items-center gap-3   px-10 py-4 text-[11px] uppercase tracking-[0.22em] font-body font-bold  transition-all">
              Start the Company Review
            </FlowButton>
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="site-content-shell">
          <SectionHeader
            eyebrow="Before you apply"
            title="Use the correct intake path"
            description="The company review is for trucking companies requesting Magellan services. Employees, contractors, assistants, sales support, writers, and operations support candidates should use the Work With Us application instead."
          />
          <FlowButton to="/work-with-us" className="inline-flex items-center gap-3 border border-ink/15 text-copy/70 px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-body hover:border-gold/40  transition-all">
            Work With Us
          </FlowButton>
        </div>
      </section>
    </PageShell>
  );
}

export function ObsolescencePage() {
  const obsoleteSignals = [
    'Driver files are incomplete or hard to find.',
    'Safety is handled after something happens.',
    'Training is verbal and inconsistent.',
    'Dispatch depends on one or two key people.',
    'Recruiting starts only when a truck is empty.',
    'The TMS is used as storage, not control.',
    'Management sees problems too late.',
    'Documents are created only when someone asks for them.',
  ];
  const modernSignals = [
    'Driver files are complete and tracked.',
    'Safety has a meeting rhythm and review process.',
    'Training is assigned, documented, and repeated.',
    'Recruiting has a pipeline.',
    'Dispatch has workflows and handoffs.',
    'The TMS supports control.',
    'Management sees KPIs before problems become emergencies.',
    'Documents are ready before audits, claims, renewals, or shipper reviews.',
  ];
  const framework = [
    ['Strategy', 'Define the kind of carrier the company is trying to become, the freight it wants to handle, the risks it must control, and the operating standards it must meet.'],
    ['Systems', 'Build the documented workflows for compliance, driver files, safety, training, recruiting, dispatch, paperwork, billing, and management reporting.'],
    ['People', 'Train drivers, dispatchers, safety managers, recruiters, and administrators into the system so the company is not dependent on memory.'],
    ['Measurement', 'Track the numbers and open actions that show whether the system is actually being followed.'],
  ];
  const engagementLevels = [
    ['Compliance Foundation', 'Core paperwork, driver files, folders, policies, checklists, renewal tracking, and audit readiness.'],
    ['Structured Growth', 'Repeatable workflows for companies adding trucks, hiring staff, and growing past memory-based operations.'],
    ['Safety Culture', 'Safety meetings, incident review, corrective action, training records, and management accountability.'],
    ['High-Value / Specialized Freight', 'Procedures, records, training, exception handling, and shipper readiness for documentation-heavy freight.'],
  ];

  return (
    <PageShell>
      <Hero
        eyebrow="Obsolescence Playbook"
        title="The old carrier model is running out of time."
        description="Paper files, undocumented training, reactive safety, hero dispatchers, and disconnected software create operational risk. Magellan helps carriers move from informal operations to documented control."
        ctaLabel="Company Review"
      />
      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <SectionHeader eyebrow="The operating shift" title="Normal inside the company can look like risk outside it" />
            <div className="space-y-5 font-body text-copy/50 leading-relaxed">
              <p>For years, many carriers survived on paper files, dispatcher memory, owner pressure, informal training, and last-minute compliance work.</p>
              <p>That model is becoming dangerous. Shippers expect more control. Insurers ask harder questions. Audits expose weak records. Drivers need clearer onboarding. Safety problems move faster than paperwork. Software alone does not fix the operating system.</p>
              <p>The Obsolescence Playbook is Magellan's framework for moving a carrier from informal operations to documented control.</p>
            </div>
          </div>
          <div className="border border-gold/20 bg-canvas p-8 md:p-10">
            <h2 className="font-display text-3xl text-ink tracking-tight mb-6">The visible freight is not the whole company.</h2>
            <p className="font-body text-copy/48 leading-relaxed">
              The business behind the freight is paperwork, policies, safety records, training, recruiting, communication, software discipline, billing handoffs, management dashboards, and daily habits under pressure.
            </p>
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <SectionHeader eyebrow="The obsolete model" title="What the old model looks like" />
            <div className="space-y-4">
              {obsoleteSignals.map((item) => (
                <p key={item} className="font-body text-copy/52 text-sm leading-relaxed border-b border-ink/8 pb-4">{item}</p>
              ))}
            </div>
          </div>
          <div>
            <SectionHeader eyebrow="The modern carrier" title="Documented, measurable, repeatable control" />
            <div className="space-y-4">
              {modernSignals.map((item) => (
                <p key={item} className="font-body text-copy/52 text-sm leading-relaxed border-b border-ink/8 pb-4">{item}</p>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell">
          <SectionHeader eyebrow="Four-part framework" title="How a carrier moves from informal operation to control" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
            {framework.map(([title, copy], index) => (
              <div key={title} className="relative p-6 border-l border-gold/20 md:border-l-0 md:border-t md:border-gold/20">
                <span className="text-accent-ink font-display text-3xl font-bold opacity-30 block mb-4">{String(index + 1).padStart(2, '0')}</span>
                <h2 className="font-display text-lg text-ink mb-3">{title}</h2>
                <p className="font-body text-copy/42 text-sm leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32">
        <div className="site-content-shell">
          <SectionHeader eyebrow="Engagement levels" title="Different carriers need different first moves" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {engagementLevels.map(([title, copy]) => (
              <div key={title} className="border border-ink/8 bg-surface p-7">
                <h2 className="font-display text-xl text-ink mb-4">{title}</h2>
                <p className="font-body text-copy/42 text-sm leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <FlowButton to="/companies/apply" className="inline-flex items-center gap-3   px-10 py-4 text-[11px] uppercase tracking-[0.22em] font-body font-bold  transition-all">
              Modernize the System Behind the Trucks
            </FlowButton>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
export function CompanyApplicationSubmittedPage() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <PageShell>
      <Hero
        eyebrow="Company review received"
        title="We received your company review."
        description="A Magellan team member will review your information and respond within 24 hours. If you need to send a scheduling question or additional context now, use the contact page."
        ctaLabel="Contact Us"
        ctaPath="/#contact"
      />
      <section className="py-24 md:py-32 bg-surface">
        <div className="site-content-shell">
          <SectionHeader eyebrow="Next step" title="What happens after submission" description="The company review request gives Magellan context before the conversation: fleet size, current systems, urgency, main operating pressure, and where the company may need structure first." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              ['Review', 'Your company review request is checked for operating gaps across paperwork, safety, training, recruiting, TMS workflow, and management visibility.'],
              ['Respond', 'A Magellan team member responds within 24 hours using the contact information you provided.'],
              ['Schedule', 'If the next step is a meeting, Magellan can coordinate the right review path after the request is checked.'],
            ].map(([title, copy], index) => (
              <div key={title} className="border border-ink/8 bg-canvas p-6">
                <span className="text-accent-ink font-display text-3xl opacity-40 block mb-4">{String(index + 1).padStart(2, '0')}</span>
                <h2 className="font-display text-xl text-ink mb-3">{title}</h2>
                <p className="font-body text-copy/45 text-sm leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
export function ResourcesPage() {
  const cmsHero = useSiteContent('resourcesHero');
  const hero = guardServicesHero(cmsHero, RESOURCES_HERO_CONTENT);
  return <PageShell className="support-pages resources-overview">
    <ResourcesLandingDesign hero={hero} topics={RESOURCES_MARQUEE_ITEMS} intro={<ResourcesIntroBand />}
      pressureAreas={RESOURCE_PRESSURE_AREAS} limits={CHECKLIST_LIMITS} faqs={RESOURCES_FAQS} />
  </PageShell>;
}

export function ResourceDetailPage({ resourceKey }) {
  const params = useParams();
  const slug = resourceKey || params.slug || '';
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setLoadError('');
    setResource(null);
    getPublishedResourcePageBySlug(slug)
      .then((cmsResource) => {
        if (!mounted) return;
        setResource(cmsResource || null);
      })
      .catch((error) => {
        console.error('Failed to load resource page:', error);
        if (!mounted) return;
        setLoadError('This resource could not be loaded right now.');
        setResource(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  return <PageShell className="support-pages resource-detail">
    <ResourceDetailDesign resource={resource} loading={loading} error={loadError}
      renderDownload={(item, offer) => <ResourceDownloadLeadForm resource={item} offer={offer} />} />
  </PageShell>;
}
